<template>
  <div class="index">
    <div class="index__container">
      <tz-breadcrumbs :items="breadcrumbs" :site-url="siteUrl" />
      <h1 class="index__title">
        {{ pageTitle }}
      </h1>
      <p v-for="names in renderIf({ condition: !pages[0] })" :key="names">
        No results found for names starting with {{ currentLetter }}. Try
        searching for a different letter or try again later.
      </p>
      <div
        v-for="names in renderIf({ condition: pages[0] })"
        :key="names"
        class="index__list"
      >
        <a
          :href="getPersonUrl(name)"
          class="index__link"
          v-for="(name, index) in pages[0]"
          :key="index"
        >
          <span class="index__name" v-text="displayFullName(name[0])" />
          <span class="index__count" v-text="`${name[2]} people`" />
        </a>
      </div>
      <tz-paginate
        v-if="hasPagination"
        :base-url="baseUrl"
        :max-pages="maxPages"
        :items-per-page="itemsPerPage"
        :total-items="totalItems"
        :current-page="currentPage"
        :displayFirstAndLast="true"
        :show-arrow-buttons="true"
        :has-trailing-slash="true"
      />
    </div>
  </div>
</template>

<script>
// Components
import TzBreadcrumbs from '@trazi/tz-breadcrumbs/src/tz-breadcrumbs.vue';
import TzPaginate from '@trazi/tz-paginate/src/tz-paginate.vue';
// Helpers
import Meta from '@/assets/js/shared/misc/meta';
import resultHelper from '@/assets/js/resultHelper';
import renderIf from '@/assets/js/shared/helpers/renderIf';
import seoSDK from '@/assets/js/shared/services/seo-sdk';

const { Fullnames } = seoSDK;
const NBars = require('nbars/commonjs.js');

const {
  seo_title: seoTitle,
  title,
  meta
} = require(`@/assets/cms/pages/names-by-letter.json`);
const siteUrl = `https://information.com`;

export default {
  name: 'index',
  components: {
    TzBreadcrumbs,
    TzPaginate
  },
  data() {
    return {
      siteUrl,
      currentPage: this.$route.params.page ? +this.$route.params.page : 1,
      currentLetter: this.$route.params.letter.toUpperCase(),
      maxPages: 10
    };
  },
  computed: {
    baseUrl() {
      return `/names-by-letter/${this.currentLetter.toLowerCase()}`;
    },
    currentUrl() {
      const url = this.baseUrl + '/';
      if (this.currentPage === 1) return url;

      return `${url}${this.currentPage}/`;
    },
    canonicalUrl() {
      return this.siteUrl + this.currentUrl;
    },
    pageTitle() {
      const pageTitle = `${title} ${this.currentLetter}`;
      if (this.currentPage === 1) return pageTitle;

      return `${pageTitle} (Page ${this.currentPage})`;
    },
    breadcrumbs() {
      const breadcrumbs = [
        {
          href: '/',
          text: 'People Directory'
        },
        {
          href: `${this.baseUrl}/`,
          text: `Last Name (${this.currentLetter})`,
          disabled: true
        }
      ];

      if (this.currentPage === 1) {
        return breadcrumbs;
      }

      breadcrumbs[1].disabled = false;
      breadcrumbs.push({
        href: this.currentUrl,
        text: `(Page ${this.currentPage})`,
        disabled: true
      });

      return breadcrumbs;
    },
    hasPagination() {
      return this.totalItems / this.itemsPerPage > 1;
    }
  },
  methods: {
    renderIf,
    /**
     *
     * @param {string} name
     * @returns {string}
     */
    getPersonUrl(name) {
      return `/people/${name[1].toLowerCase()}/`;
    },
    /**
     *
     * @param {string} name
     * @returns {string}
     */
    displayFullName(name) {
      return resultHelper.formatName(name);
    }
  },
  async asyncData({ $bugsnag, params }) {
    if (!process.server) {
      return;
    }
    const payload = {};
    const { letter, page } = params;

    try {
      payload.fullNames = await Fullnames.byLetterPage(letter, page || 1);
    } catch (err) {
      payload.fullNames = {};
      console.error(err);
      $bugsnag?.notify(err);
    }

    const {
      totalPages = 0,
      itemsPerPage = 0,
      totalItems = 0,
      pages = []
    } = payload.fullNames;

    return { totalPages, itemsPerPage, totalItems, pages };
  },
  head() {
    const transformedMeta = [];
    const letter =
      this.currentPage === 1
        ? this.currentLetter
        : `${this.currentLetter} (Page ${this.currentPage})`;
    for (let i = 0; i < meta.length; i++) {
      const metaCopy = Object.assign({}, meta[i]);
      const { hid, content } = metaCopy;
      if (hid === 'og:url') {
        metaCopy.content = this.canonicalUrl;
      } else if (
        hid === 'og:title' ||
        hid === 'og:description' ||
        hid === 'description'
      ) {
        metaCopy.content = NBars.transform(content, { letter });
      }
      transformedMeta[i] = metaCopy;
    }

    return {
      title: NBars.transform(seoTitle, { letter }),
      meta: Meta.convertToNuxtMeta(transformedMeta)
    };
  }
};
</script>
